<template>
  <div class="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div class="glass-strong rounded-xl p-6 w-full max-w-lg mx-4">
      <h3 class="text-xl font-bold text-white mb-4">Reservation</h3>
      <div class="space-y-3 text-slate-300 text-sm">
        <div>
          <span class="text-slate-400">Equipment:</span>
          {{ reservation.equipment_name || '—' }}
          <span
            v-if="reservation.equipment_serial"
            class="ml-2 font-mono text-xs px-2 py-0.5 rounded border border-slate-700 bg-slate-900/60 text-slate-200"
            >{{ reservation.equipment_serial }}</span
          >
        </div>
        <div class="border-t border-slate-800 pt-2">
          <div class="text-slate-400">Where/Who</div>
          <div class="mt-1">{{ reservation.organization_name || '—' }}</div>
          <div v-if="reservation.contact_name" class="text-slate-400 text-xs">
            {{ reservation.contact_name
            }}<span v-if="reservation.contact_title">, {{ reservation.contact_title }}</span>
          </div>
          <div v-if="fullAddress" class="mt-1 flex items-center gap-2">
            <span class="text-slate-300 text-xs">{{ fullAddress }}</span>
            <a :href="mapHref" target="_blank" class="text-primary-400 text-xs underline">Map</a>
            <button @click="copyAddress" class="text-slate-300 text-xs underline">Copy</button>
          </div>
        </div>
        <div><span class="text-slate-400">Purpose:</span> {{ reservation.purpose }}</div>
        <div>
          <span class="text-slate-400">Start:</span>
          {{ new Date(reservation.start_at).toLocaleString() }}
        </div>
        <div>
          <span class="text-slate-400">End:</span>
          {{ new Date(reservation.end_at).toLocaleString() }}
        </div>
        <div v-if="msg" class="text-amber-400">{{ msg }}</div>
      </div>
      <div class="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label class="block text-slate-300 mb-1">Extend End</label>
          <input
            v-model="newEnd"
            type="datetime-local"
            class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
          />
        </div>
        <div>
          <label class="block text-slate-300 mb-1">Notes</label>
          <input
            v-model="notes"
            class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded-lg text-white"
          />
        </div>
      </div>
      <div class="flex space-x-3 mt-6">
        <button
          @click="$emit('close')"
          class="flex-1 px-4 py-2 bg-slate-800/70 border border-slate-700 text-white rounded-lg"
        >
          Close
        </button>
        <button @click="save" class="flex-1 px-4 py-2 rounded-lg btn-primary">Save</button>
        <button @click="cancelRes" class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { supabase } from '@/lib/supabaseClient'

const props = defineProps<{ reservation: any }>()
const emit = defineEmits(['close', 'saved'])

const newEnd = ref('')
const notes = ref('')
const msg = ref('')

const fullAddress = computed(() => {
  const a = [
    props.reservation.address1,
    props.reservation.city,
    props.reservation.state,
    props.reservation.zip,
  ]
    .filter(Boolean)
    .join(', ')
  return a || ''
})
const mapHref = computed(() =>
  fullAddress.value ? `https://maps.google.com/?q=${encodeURIComponent(fullAddress.value)}` : '#',
)
function copyAddress() {
  try {
    navigator.clipboard.writeText(fullAddress.value)
  } catch {}
}

async function save() {
  msg.value = ''
  const updates: any = {}
  if (newEnd.value) updates.end_at = new Date(newEnd.value).toISOString()
  if (notes.value) updates.notes = notes.value
  if (Object.keys(updates).length === 0) return
  const { error } = await supabase
    .schema('app' as any).from('reservations')
    .update(updates)
    .eq('id', props.reservation.id)
  if (error) {
    msg.value = error.message
  } else {
    emit('saved')
    emit('close')
  }
}

async function cancelRes() {
  msg.value = ''
  const { error } = await supabase
    .schema('app' as any).from('reservations')
    .delete()
    .eq('id', props.reservation.id)
  if (error) {
    msg.value = error.message
  } else {
    emit('saved')
    emit('close')
  }
}
</script>
